/*
 * Copyright (C) 2024 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Inject, Injectable, NgZone } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  MENTOR_MENTEE_DATA_SERVICE,
  MentorFeedback,
  MentorMenteeDataService,
} from '@app/app/page-modules/admin/services/mentor-mentee/mentor-mentee-data.service';
import { ObservableResult } from '@app/app/shared/store';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { map, switchMap, tap } from 'rxjs/operators';
import { SnackbarHelper } from '../../../shared/helpers/snackbar-helper';
import { Form, MemberReview, Review } from '../../../shared/models';
import { MEMBERS_DATA_SERVICE, MembersDataService } from '../../admin/services/members/members-data.service';
import { ReviewCard, ReviewItem, ReviewSummaryForReviewer, SurroundingUserReview } from '../models';
import { REVIEWS_DATA_SERVICE, ReviewsDataService } from '../services/data.service';
import * as ReviewsActions from './reviews.actions';
import { LoadMentorFeedback, LoadReviewCardsOnly } from './reviews.actions';
import { INITIAL_STATE, ReviewsStateModel } from './reviews.state.model';

@State<ReviewsStateModel>({
  name: 'reviews',
  defaults: INITIAL_STATE,
})
@Injectable()
export class ReviewsState {
  @Selector()
  static review({ review }: ReviewsStateModel): Review {
    return review;
  }

  @Selector()
  static reviewItem({ reviewItem }: ReviewsStateModel): ReviewItem {
    return reviewItem;
  }

  @Selector()
  static reviewCards({ reviewCards }: ReviewsStateModel): ReviewCard[] {
    return reviewCards;
  }

  @Selector()
  static reviewSummaryForReviewer({ reviewSummaryForReviewer }: ReviewsStateModel): ReviewSummaryForReviewer {
    return reviewSummaryForReviewer;
  }

  @Selector()
  static surroundingUserReviews({ surroundingUserReviews }: ReviewsStateModel): SurroundingUserReview[] {
    return surroundingUserReviews;
  }

  @Selector()
  static mentorFeedback({ mentorFeedback }: ReviewsStateModel): MentorFeedback {
    return mentorFeedback;
  }

  constructor(
    private ngZone: NgZone,
    private snackBar: MatSnackBar,
    @Inject(MEMBERS_DATA_SERVICE) private membersDataService: MembersDataService,
    @Inject(REVIEWS_DATA_SERVICE) private reviewsDataService: ReviewsDataService,
    @Inject(MENTOR_MENTEE_DATA_SERVICE) private mentorMenteeDataService: MentorMenteeDataService,
  ) {}

  @Action(ReviewsActions.LoadReviewCardsOnly)
  loadReviewCardsOnly({ patchState }: StateContext<ReviewsStateModel>, { itemType, userUid, itemUid }: LoadReviewCardsOnly) {
    if (itemType === 'CARD') {
      return this.loadLearnerCardContent(itemUid, userUid, patchState);
    } else {
      return this.loadLearnerPlaylistContent(itemUid, userUid, patchState);
    }
  }

  @Action(ReviewsActions.LoadMentorFeedback)
  loadMentorFeedback({ patchState }: StateContext<ReviewsStateModel>, { playlistUid, learnerUserUid, mentorUserUid }: LoadMentorFeedback) {
    this.mentorMenteeDataService
      .getSingleMentorFeedbackForLearnerPlaylist(playlistUid, learnerUserUid, mentorUserUid)
      .subscribe(({ isSuccess, value, error }) => {
        if (isSuccess) {
          patchState({ mentorFeedback: value });
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
      });
  }

  @Action(ReviewsActions.LoadReviewDetails)
  setReview({ patchState }: StateContext<ReviewsStateModel>, { reviewItemUri, learnerUserUid }) {
    /*
     Changed from a subscription to returning an Observable with the state updated using the `tap` operator.
     This makes it possible to catch the error in the component which dispatches this action, as the error will be rethrown
     in the Observable returned from `store.dispatch()`.
    */
    return this.reviewsDataService.getReviewSummaryDetailed(reviewItemUri, learnerUserUid).pipe(
      tap(({ isSuccess, value, error }) => {
        if (isSuccess && value) {
          patchState({
            review: value.review,
            reviewItem: value.reviewItem,
            reviewSummaryForReviewer: value.reviewSummaryForReviewer,
          });
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
      }),
      ObservableResult.unwrapResult(),
      switchMap((value) => {
        switch (value.reviewItem.reviewItemType) {
          case 'CARD':
            return this.loadLearnerCardContent(value.reviewItem.uid, learnerUserUid, patchState).pipe(ObservableResult.unwrapResult());
          case 'PLAYLIST':
            return this.loadLearnerPlaylistContent(value.reviewItem.uid, learnerUserUid, patchState).pipe(ObservableResult.unwrapResult());
          // Assignment items route straight to the course marking screen, never through this flow.
          case 'ASSIGNMENT':
            throw new Error('Assignment items are not reviewed in this flow');
        }
      }),
      map(() => void 0),
    );
  }

  @Action(ReviewsActions.SetSurroundingUserReviews)
  setSurroundingUserReviews({ patchState }: StateContext<ReviewsStateModel>, { membersReviews }) {
    const memberReviewsArray = membersReviews as MemberReview[];
    const surroundingUserReviews: SurroundingUserReview[] = [];

    memberReviewsArray.forEach((memberReview, index) => {
      const userUid = memberReview.userUid;
      let previousUserUid = undefined;
      let nextUserUid = undefined;

      if (index - 1 >= 0) {
        previousUserUid = memberReviewsArray[index - 1].userUid;
      }

      if (index + 1 < memberReviewsArray.length) {
        nextUserUid = memberReviewsArray[index + 1].userUid;
      }

      surroundingUserReviews.push({
        previousUserUid: previousUserUid,
        userUid: userUid,
        nextUserUid: nextUserUid,
      });
    });

    patchState({
      surroundingUserReviews: surroundingUserReviews,
    });
  }

  private loadLearnerPlaylistContent(
    playlistUid: string,
    learnerUserUid: string,
    patchState: (p: Partial<ReviewsStateModel>) => Partial<ReviewsStateModel>,
  ) {
    return this.membersDataService.getPlaylistLearnerCardsByUserId(playlistUid, learnerUserUid).pipe(
      tap(({ isSuccess, value, error }) => {
        if (isSuccess && value) {
          patchState({
            reviewCards: value
              .filter((card) => card.learnerFormAnswers.length > 0)
              .map((card) => ({
                title: card.title,
                uri: card.uri,
                cardUid: card.cardUid,
                learnerFormAnswers: card.learnerFormAnswers.map((formAnswer) => ({
                  uid: formAnswer.formUid,
                  answer: formAnswer,
                  form: {
                    uid: formAnswer.formUid,
                    content: {
                      ...formAnswer,
                      userAnswer: formAnswer.answer,
                      formIsActive: true,
                      notes: formAnswer.notes.sort((a, b) => {
                        if (a.addedOn < b.addedOn) {
                          return -1;
                        } else if (a.addedOn > b.addedOn) {
                          return 1;
                        } else {
                          return 0;
                        }
                      }),
                      wordLimit: formAnswer.wordLimit,
                      answerType: 'RICH',
                      title: formAnswer.instruction,
                      type: formAnswer.type,
                    },
                    type: 'FORM',
                  } as Form,
                })),
                required: card.required,
                completed: card.completed,
                hasCardReviewEnabled: card.hasCardReviewEnabled,
              })),
          });
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
      }),
    );
  }

  private loadLearnerCardContent(
    cardUid: string,
    learnerUserUid: string,
    patchState: (p: Partial<ReviewsStateModel>) => Partial<ReviewsStateModel>,
  ) {
    return this.membersDataService.getLearnerCardForReviewer(learnerUserUid, cardUid).pipe(
      tap(({ isSuccess, value, error }) => {
        if (isSuccess && value) {
          let reviewCards = [];
          if (value.learnerFormAnswers.length > 0) {
            reviewCards = [
              {
                title: value.title,
                uri: value.uri,
                cardUid: value.cardUid,
                learnerFormAnswers: value.learnerFormAnswers.map((formAnswer) => ({
                  uid: formAnswer.formUid,
                  answer: formAnswer,
                  form: {
                    uid: formAnswer.formUid,
                    content: {
                      ...formAnswer,
                      userAnswer: formAnswer.answer,
                      formIsActive: true,
                      notes: formAnswer.notes.sort((a, b) => {
                        if (a.addedOn < b.addedOn) {
                          return -1;
                        } else if (a.addedOn > b.addedOn) {
                          return 1;
                        } else {
                          return 0;
                        }
                      }),
                      wordLimit: formAnswer.wordLimit,
                      answerType: 'RICH',
                      title: formAnswer.instruction,
                      type: formAnswer.type,
                    },
                    type: 'FORM',
                  } as Form,
                })),
                required: value.required,
                completed: value.completed,
                hasCardReviewEnabled: value.hasCardReviewEnabled,
              },
            ];
          }

          patchState({ reviewCards: reviewCards });
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
      }),
    );
  }

  @Action(ReviewsActions.SetReviewItem)
  setReviewItem({ patchState }: StateContext<ReviewsStateModel>, { reviewItem }) {
    patchState({
      reviewItem: reviewItem,
    });
  }

  @Action(ReviewsActions.ClearReviewsState)
  clearReviewsState({ setState, getState }: StateContext<ReviewsStateModel>, { clearSurroundingUserReviews }) {
    setState({
      review: INITIAL_STATE.review,
      reviewItem: INITIAL_STATE.reviewItem,
      reviewCards: INITIAL_STATE.reviewCards,
      reviewSummaryForReviewer: INITIAL_STATE.reviewSummaryForReviewer,
      surroundingUserReviews: clearSurroundingUserReviews ? INITIAL_STATE.surroundingUserReviews : getState().surroundingUserReviews,
      mentorFeedback: INITIAL_STATE.mentorFeedback,
    });
  }
}
